import { DIALOG_DATA, DialogRef } from '@angular/cdk/dialog';
import {
  Component,
  computed,
  inject,
  signal,
  viewChildren,
} from '@angular/core';
import { TranslatePipe } from '@ngx-translate/core';
import {
  OnyxButtonComponent,
  OnyxModalComponent,
  OnyxToastService,
  OnyxTooltipDirective,
} from '@onyx/angular';
import {
  catchError,
  concatMap,
  EMPTY,
  filter,
  finalize,
  forkJoin,
  from,
  Subject,
  switchMap,
  toArray,
} from 'rxjs';
import { ROUTE_CONTEXT } from '../../../../../common/services/route.service';
import { TelematicsStore } from '../../../../../common/stores/telematics.store';
import type { SimplifiedFleet } from '../../../../fleet/common/interfaces/fleet';
import { SwapStatusFormSetComponent } from './swap-status-form-set/swap-status-form-set.component';

export interface SwapStatusModalData {
  swapUuid: string;
  vehicleSet: {
    vehicle: SimplifiedFleet;
    trailer: SimplifiedFleet | null;
  };
  otherVehicleSet: {
    vehicle: SimplifiedFleet;
    trailer: SimplifiedFleet | null;
  } | null;
}

@Component({
  selector: 'app-swap-status-modal',
  imports: [
    OnyxModalComponent,
    OnyxButtonComponent,
    TranslatePipe,
    OnyxTooltipDirective,
    SwapStatusFormSetComponent,
  ],
  providers: [{ provide: ROUTE_CONTEXT, useValue: 'map' }],
  templateUrl: './swap-status-modal.component.html',
  styleUrl: './swap-status-modal.component.scss',
})
export class SwapStatusModalComponent {
  protected readonly data = inject<SwapStatusModalData>(DIALOG_DATA);
  protected readonly dialogRef = inject(DialogRef);
  protected readonly telematicsStore = inject(TelematicsStore);
  private readonly toastService = inject(OnyxToastService);

  private readonly sets = viewChildren(SwapStatusFormSetComponent);

  protected readonly I18N = 'map.swapStatusModal';

  protected vehicleSets = [
    this.data.vehicleSet,
    ...(this.data.otherVehicleSet ? [this.data.otherVehicleSet] : []),
  ];
  protected isDouble = this.vehicleSets.length > 1;

  protected loading = signal(false);
  protected close$ = new Subject<void>();

  protected isDisabled = computed(
    () =>
      this.sets().length > 0 && this.sets().every((set) => set.isDisabled()),
  );
  protected tooltip = computed(() => {
    if (!this.isDisabled()) return undefined;

    return this.sets()
      .find((set) => set.isDisabled())
      ?.tooltip();
  });

  protected submit(): void {
    if (this.loading() || this.isDisabled()) return;

    const sets = this.sets().filter((set) => !set.isDisabled());
    if (!sets.length) return;

    this.loading.set(true);
    forkJoin(sets.map((set) => set.validate()))
      .pipe(
        filter((validations) => validations.every(Boolean)),
        switchMap(() =>
          from(sets).pipe(
            concatMap((set) => set.submit()),
            toArray(),
            filter((results) => results.length === sets.length),
          ),
        ),
        catchError(() => EMPTY),
        finalize(() => this.loading.set(false)),
      )
      .subscribe(() => {
        this.toastService.showSuccess({
          message: 'route.statusForm.success',
        });
        this.close$.next();
      });
  }
}
